import React, { useState } from "react"

import { useNavigate } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { useApplication } from "@/contexts/ApplicationContext"
import { useToast } from "@/hooks/use-toast"
import useDriverOnroadTestAssessment from "@/hooks/useDriverOnroadTestAssessment"
import { submitDriverOnroadTest } from "@/services/cloudFunctionService"

import ColorMatch from "./ColorMatch"
import DynamicRoutePlanning, { Round } from "./DynamicRoutePlanning"
import ParcelSort from "./ParcelSort"
import RoutePlanning from "./RoutePlanning"
import TextChoice from "./TextChoice"
import VisualReaction from "./VisualReaction"

const COLORS = {
  honeycombYellow: "#FFBE37",
  darkJungleGreen: "#0F1D21",
  skyBlue: "#BCDEEF",
  greenCorrect: "#10B981",
  redIncorrect: "#EF4444",
  lightGreen: "#D1FAE5",
  lightYellow: "#FDE68A",
  lightRed: "#FEE2E2",
  neutralGray: "#F0F0F0",
  darkGrayText: "#0F1D21",
  accentPurple: "#6366F1",
  lightBlueGray: "#E2E8F0",
  mediumGrayDashed: "#94A3B8",
  lightBackground: "#F8FAFC",
  lightSlotBackground: "#F0F4F8",
}

const quizData = [
  // Knowledge of Traffic Laws (2 questions)
  {
    id: 1,
    type: "text-choice",
    section: "Knowledge of Traffic Laws",
    question:
      "You are driving on a road with street lighting. In the absence of other signs, what is the national speed limit?",
    options: ["20 mph", "30 mph", "40 mph", "60 mph"],
    answer: "30 mph",
  },
  {
    id: 2,
    type: "text-choice",
    section: "Knowledge of Traffic Laws",
    question: "What is the legal requirement for tyre tread depth for a car in the UK?",
    options: [
      "A minimum of 1mm across the central three-quarters of the tread.",
      "A minimum of 1.6mm across the central three-quarters of the tread.",
      "A minimum of 2mm across the entire tread.",
      "There is no legal minimum as long as the tyre is not bald.",
    ],
    answer: "A minimum of 1.6mm across the central three-quarters of the tread.",
  },
  // Defensive Driving (3 questions)
  {
    id: 3,
    type: "text-choice",
    section: "Defensive Driving",
    question:
      "You are driving in heavy rain, and your visibility is significantly reduced. What is the most crucial adjustment you should make?",
    options: [
      "Turn on your fog lights.",
      "Increase your following distance from the vehicle in front.",
      "Maintain your speed to get out of the rain quicker.",
      "Use your hazard lights to warn other drivers.",
    ],
    answer: "Increase your following distance from the vehicle in front.",
  },
  {
    id: 4,
    type: "text-choice",
    section: "Defensive Driving",
    question:
      "A ball bounces into the road from between two parked cars. What is the safest and most immediate action to take?",
    options: [
      "Swerve sharply to avoid the ball.",
      "Sound your horn loudly.",
      "Brake firmly and be prepared for a child to follow the ball.",
      "Speed up to pass the area quickly.",
    ],
    answer: "Brake firmly and be prepared for a child to follow the ball.",
  },
  {
    id: 5,
    type: "text-choice",
    section: "Defensive Driving",
    question:
      "When approaching a cyclist, what is the recommended minimum passing distance you should give them in a 50 mph zone?",
    options: [
      "At least 0.5 metres.",
      "At least 1.0 metre.",
      "At least 1.5 metres.",
      "As much room as you would give a car.",
    ],
    answer: "As much room as you would give a car.",
  },
  // Vehicle Handling and Control (3 questions)
  {
    id: 6,
    type: "text-choice",
    section: "Vehicle Handling and Control",
    question:
      "If your vehicle begins to skid on a wet or icy surface, what is the correct action to take?",
    options: [
      "Brake hard and steer away from the skid.",
      "Accelerate gently to regain traction.",
      "Steer gently into the skid (i.e., if the rear of the car skids right, steer right).",
      "Take your hands off the steering wheel and let the car correct itself.",
    ],
    answer: "Steer gently into the skid (i.e., if the rear of the car skids right, steer right).",
  },
  {
    id: 7,
    type: "text-choice",
    section: "Vehicle Handling and Control",
    question:
      "You are driving a vehicle with an automatic transmission and need to descend a long, steep hill. What is the best technique to control your speed?",
    options: [
      "Use the foot brake continuously.",
      "Select a lower gear to use engine braking.",
      "Shift into Neutral to save fuel.",
      "Turn off the engine and coast.",
    ],
    answer: "Select a lower gear to use engine braking.",
  },
  {
    id: 8,
    type: "text-choice",
    section: "Vehicle Handling and Control",
    question: "Before starting any journey, what is the most important pre-drive check to perform?",
    options: [
      "Check that the radio is working.",
      "Ensure all windows and mirrors are clean and unobstructed.",
      "Verify you have your favourite snacks for the journey.",
      "Check the vehicle's paintwork for scratches.",
    ],
    answer: "Ensure all windows and mirrors are clean and unobstructed.",
  },
  // Situational Awareness & Sustained Attention (3 visual-reaction questions)
  {
    id: 9,
    type: "visual-reaction",
    section: "Situational Awareness & Sustained Attention",
    question:
      "Click the circle ONLY when it turns YELLOW. Do NOT click if it turns any other color.",
    sequence: ["blue", "yellow", "blue", "yellow", "blue", "yellow"],
    targetColor: "yellow",
    minDelay: 1050, // ~30% harder from 1500
    maxDelay: 2800, // ~30% harder from 4000
  },
  {
    id: 10,
    type: "visual-reaction",
    section: "Situational Awareness & Sustained Attention",
    question:
      "Click the circle ONLY when it turns YELLOW. Do NOT click if it turns any other color.",
    sequence: [
      "blue",
      "red",
      "yellow",
      "green",
      "yellow",
      "blue",
      "yellow",
      "red",
      "yellow",
      "green",
    ],
    targetColor: "yellow",
    minDelay: 560, // ~30% harder from 800
    maxDelay: 1750, // ~30% harder from 2500
  },
  {
    id: 11,
    type: "visual-reaction",
    section: "Situational Awareness & Sustained Attention",
    question:
      "Click the circle ONLY when it turns YELLOW. Do NOT click if it turns any other color.",
    sequence: [
      "red",
      "blue",
      "green",
      "yellow",
      "red",
      "yellow",
      "blue",
      "green",
      "yellow",
      "red",
      "blue",
      "yellow",
      "green",
      "red",
      "yellow",
    ],
    targetColor: "yellow",
    minDelay: 350, // ~30% harder from 500
    maxDelay: 1050, // ~30% harder from 1500
  },
  // Parcel Sorting (2 questions)
  {
    id: 12,
    type: "parcel-sort-asc",
    section: "Reaction Times & Decision Accuracy",
    question: "Click the parcels in ascending numerical order (low to high).",
    rangeStart: 1,
    rangeEnd: 20,
    displayCount: 12, // Display 12 parcels at once for the user to sort
  },
  {
    id: 13,
    type: "parcel-sort-desc",
    section: "Reaction Times & Decision Accuracy",
    question: "Click the parcels in descending numerical order (high to low).",
    rangeStart: 78,
    rangeEnd: 96,
    displayCount: 12, // Display 12 parcels at once
  },
  // Color/Text Matching (1 question with 3 rounds)
  {
    id: 14,
    type: "color-match",
    section: "Task-Switching Ability & Sustained Attention",
    question: "Match the color to the right box. Follow the rule for each round.",
    rounds: [
      {
        instruction: "Click the box where the COLOR of the box matches the TEXT written on it.",
        targetColor: "blue", // This is the color of the box
        targetText: "BLUE", // This is the text in the box
        choices: [
          { color: "red", text: "RED" },
          { color: "blue", text: "BLUE" },
          { color: "green", text: "GREEN" },
          { color: "yellow", text: "YELLOW" },
        ],
      },
      {
        instruction:
          "Click the box where the TEXT describes the COLOR of the box, even if the text color is different.",
        targetColor: "green",
        targetText: "GREEN",
        choices: [
          { color: "red", text: "BLUE" },
          { color: "blue", text: "BLUE" },
          { color: "green", text: "RED" },
          { color: "yellow", text: "YELLOW" },
        ],
      },
      {
        instruction:
          "Click the box where the COLOR of the box is NOT the same as the TEXT written on it.",
        targetColor: "red",
        targetText: "RED",
        choices: [
          { color: "red", text: "RED" },
          { color: "blue", text: "GREEN" },
          { color: "green", text: "BLUE" },
          { color: "yellow", text: "YELLOW" },
        ],
      },
    ],
  },
  // Route Accuracy Challenge (Map Matching) - Level 1
  {
    id: 15,
    type: "route-accuracy",
    section: "Route Accuracy & Visual Search",
    question:
      "Match each parcel (e.g., P1) to its corresponding delivery point (e.g., A). Colors are for visual distinction only.",
    parcels: [
      {
        id: "P1",
        destination: "A",
        label: "P1",
        color: COLORS.honeycombYellow,
      },
      { id: "P2", destination: "B", label: "P2", color: COLORS.skyBlue },
      { id: "P3", destination: "C", label: "P3", color: COLORS.accentPurple },
      { id: "P4", destination: "D", label: "P4", color: COLORS.greenCorrect },
      { id: "P5", destination: "E", label: "P5", color: "#F472B6" } /* Pink */,
      {
        id: "P6",
        destination: "F",
        label: "P6",
        color: "#60A5FA",
      } /* Light Blue */,
      { id: "P7", destination: "G", label: "P7", color: "#FACC15" } /* Amber */,
      {
        id: "P8",
        destination: "H",
        label: "P8",
        color: "#A78BFA",
      } /* Violet */,
    ],
    mapPoints: [
      {
        id: "A",
        label: "A",
        top: "10%",
        left: "15%",
        color: COLORS.darkJungleGreen,
      },
      {
        id: "B",
        label: "B",
        top: "25%",
        left: "80%",
        color: COLORS.honeycombYellow,
      },
      { id: "C", label: "C", top: "50%", left: "20%", color: COLORS.skyBlue },
      {
        id: "D",
        label: "D",
        top: "70%",
        left: "75%",
        color: COLORS.redIncorrect,
      },
      { id: "E", label: "E", top: "30%", left: "45%", color: "#F472B6" },
      { id: "F", label: "F", top: "85%", left: "10%", color: "#60A5FA" },
      { id: "G", label: "G", top: "55%", left: "60%", color: "#FACC15" },
      { id: "H", label: "H", top: "15%", left: "60%", color: "#A78BFA" },
    ],
  },
  // Route Accuracy Challenge (Map Matching) - Level 2
  {
    id: 16,
    type: "route-accuracy",
    section: "Route Accuracy & Visual Search",
    question:
      "Match each parcel (e.g., P1) to its corresponding delivery point (e.g., A). Colors are for visual distinction only.",
    parcels: [
      {
        id: "P1",
        destination: "A",
        label: "P1",
        color: COLORS.honeycombYellow,
      },
      { id: "P2", destination: "B", label: "P2", color: COLORS.skyBlue },
      { id: "P3", destination: "C", label: "P3", color: COLORS.accentPurple },
      { id: "P4", destination: "D", label: "P4", color: COLORS.greenCorrect },
      { id: "P5", destination: "E", label: "P5", color: "#F472B6" },
      { id: "P6", destination: "F", label: "P6", color: "#60A5FA" },
      { id: "P7", destination: "G", label: "P7", color: "#FACC15" },
      { id: "P8", destination: "H", label: "P8", color: "#A78BFA" },
      { id: "P9", destination: "I", label: "P9", color: "#F87171" },
      { id: "P10", destination: "J", label: "P10", color: "#34D399" },
      { id: "P11", destination: "K", label: "P11", color: "#A8A29E" },
      { id: "P12", destination: "L", label: "P12", color: "#FB923C" },
    ],
    mapPoints: [
      {
        id: "A",
        label: "A",
        top: "5%",
        left: "10%",
        color: COLORS.darkJungleGreen,
      },
      {
        id: "B",
        label: "B",
        top: "15%",
        left: "85%",
        color: COLORS.honeycombYellow,
      },
      { id: "C", label: "C", top: "40%", left: "10%", color: COLORS.skyBlue },
      {
        id: "D",
        label: "D",
        top: "65%",
        left: "85%",
        color: COLORS.redIncorrect,
      },
      { id: "E", label: "E", top: "20%", left: "40%", color: "#F472B6" },
      { id: "F", label: "F", top: "90%", left: "5%", color: "#60A5FA" },
      { id: "G", label: "G", top: "50%", left: "70%", color: "#FACC15" },
      { id: "H", label: "H", top: "30%", left: "65%", color: "#A78BFA" },
      { id: "I", label: "I", top: "75%", left: "20%", color: "#F87171" },
      { id: "J", label: "J", top: "10%", left: "55%", color: "#34D399" },
      { id: "K", label: "K", top: "80%", left: "50%", color: "#A8A29E" },
      { id: "L", label: "L", top: "45%", left: "35%", color: "#FB923C" },
    ],
  },
  // Dynamic Route Planning Challenge (3 Rounds)
  {
    id: 17,
    type: "dynamic-route-planning",
    section: "Route Optimization & Adaptability",
    question:
      "Navigate from the WAREHOUSE to the START HOUSE(S). Roads may close, forcing you to re-route! Click adjacent cells to move.",
    mapGridSize: 7, // 7x7 grid for all rounds
    rounds: [
      {
        // Round 1: Easy (1 House)
        name: "Round 1 - 1 House",
        roundTimeout: 60000, // 60 seconds
        startPoint: { row: 6, col: 6, label: "Warehouse" },
        endPoints: [{ row: 0, col: 0, label: "House 1" }],
        initialBlockedPoints: [
          { row: 3, col: 3, type: "roadblock" }, // Central obstacle
        ],
        dynamicClosures: [
          { point: { row: 5, col: 6 }, type: "roadblock", delay: 3000 }, // Blocks a path from start
          { point: { row: 1, col: 3 }, type: "roadblock", delay: 6000 }, // Blocks a path from start
        ],
      },
      {
        // Round 2: Medium-Hard (2 Houses)
        name: "Round 2 - 2 Houses",
        roundTimeout: 60000, // 60 seconds
        startPoint: { row: 6, col: 6, label: "Warehouse" },
        endPoints: [
          { row: 0, col: 0, label: "House 1" },
          { row: 6, col: 0, label: "House 2" },
        ],
        initialBlockedPoints: [
          { row: 3, col: 3, type: "roadblock" },
          { row: 1, col: 4, type: "roadblock" }, // Upper right obstacle
          { row: 4, col: 1, type: "roadblock" }, // Lower left obstacle
        ],
        dynamicClosures: [
          { point: { row: 5, col: 5 }, type: "roadblock", delay: 3000 },
          { point: { row: 6, col: 5 }, type: "roadblock", delay: 3000 },
          { point: { row: 2, col: 0 }, type: "tunnel", delay: 7000 }, // Blocks a path near House 1
        ],
      },
      {
        // Round 3: Hard (3 Houses)
        name: "Round 3 - 3 Houses",
        roundTimeout: 10000, // 10 seconds for Hard round
        startPoint: { row: 6, col: 6, label: "Warehouse" },
        endPoints: [
          { row: 0, col: 0, label: "House 1" },
          { row: 6, col: 0, label: "House 2" },
          { row: 3, col: 3, label: "House 3" }, // Central house
        ],
        initialBlockedPoints: [
          // These are designed to force winding paths but ensure solvability
          { row: 1, col: 1, type: "roadblock" },
          { row: 1, col: 2, type: "roadblock" },
          { row: 2, col: 1, type: "roadblock" },
          { row: 2, col: 4, type: "roadblock" },
          { row: 4, col: 2, type: "roadblock" },
          // { row: 4, col: 5, type: "roadblock" },
          { row: 5, col: 4, type: "roadblock" },
          { row: 0, col: 5, type: "roadblock" },
          { row: 5, col: 0, type: "roadblock" },
        ],
        dynamicClosures: [
          { point: { row: 6, col: 4 }, type: "roadblock", delay: 3000 }, // Blocks path from start
          { point: { row: 4, col: 6 }, type: "roadblock", delay: 3000 }, // Blocks path from start
          { point: { row: 0, col: 1 }, type: "tunnel", delay: 7000 }, // Blocks path to House 1 (tunnel)
          { point: { row: 3, col: 5 }, type: "roadblock", delay: 11000 }, // Blocks central area path
        ],
      },
    ],
  },
]

// Type definitions for scoring
interface SectionScore {
  total: number
  correct: number
}

interface QuestionScore {
  score: number
  reactionTimes?: number[]
  timeTaken?: number
  answer?: string
  isCorrect?: boolean
}

interface TestResults {
  totalScore: number
  totalPossibleScore: number
  scorePercentage: number
  sectionScores: Record<string, SectionScore>
  riskLevel: {
    level: string
    class: string
  }
  averageReactionTime?: number
}

// Calculate total possible score dynamically like in NewTest.html
const calculateTotalPossibleScore = () => {
  let total = 0
  quizData.forEach((q) => {
    if (q.type === "visual-reaction") {
      total += q.sequence.filter((color) => color === q.targetColor).length
    } else if (q.type === "parcel-sort-asc" || q.type === "parcel-sort-desc") {
      total += q.displayCount
    } else if (q.type === "color-match") {
      total += q.rounds.length
    } else if (q.type === "route-accuracy") {
      total += q.parcels.length
    } else if (q.type === "dynamic-route-planning") {
      q.rounds.forEach((round) => {
        total += round.endPoints.length
      })
    } else {
      total++
    }
  })
  return total
}

// Determine risk level based on score percentage
const determineRiskLevel = (overallScorePercentage: number) => {
  if (overallScorePercentage >= 80) {
    return { level: "Low-Risk Candidate", class: "risk-low" }
  } else if (overallScorePercentage >= 60) {
    return { level: "Medium-Risk Candidate", class: "risk-medium" }
  } else {
    return { level: "High-Risk Candidate", class: "risk-high" }
  }
}

const DriverOnroadTest: React.FC = () => {
  const [started, setStarted] = useState(false)
  const [currentQuestion, setCurrentQuestion] = useState(1)
  const { application, refetch } = useApplication()
  const [answers, setAnswers] = useState<
    Record<number, string | QuestionScore | Array<QuestionScore>>
  >({})

  // Store current answer data without calculating section scores yet
  const [currentAnswerData, setCurrentAnswerData] = useState<{
    option?: string
    isCorrect?: boolean
    score?: number
    section?: string
    questionType?: string
    reactionTimes?: number[]
    timeTaken?: number
    totalTargets?: number
    displayCount?: number
    roundsLength?: number
    parcelsLength?: number
    endPointsLength?: number
  } | null>(null)

  // Track section scores similar to NewTest.html
  const [sectionScores, setSectionScores] = useState<Record<string, SectionScore>>({})
  const { toast } = useToast()
  const navigate = useNavigate()

  // Assessment mutations using driver onroad test assessment hook
  const {
    startAssessment: startDriverOnroadAssessment,
    updateQuestion: updateDriverOnroadQuestion,
    assessmentId: _driverOnroadAssessmentId,
    isLoading: _isDriverOnroadAssessmentLoading,
  } = useDriverOnroadTestAssessment()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleStartQuiz = () => {
    // Record the assessment start time if we have an application ID
    if (application?.applicationId) {
      startDriverOnroadAssessment().then((result) => {
        if (result.success) {
          toast({
            title: "Assessment Started",
            description: "Driver onroad test assessment started successfully.",
          })
          console.log("Driver onroad test assessment started with ID:", result.assessmentId)
          setStarted(true)
        } else {
          toast({
            title: "Assessment Failed",
            description: "Failed to start driver onroad test assessment.",
            variant: "destructive",
          })
          console.error("Failed to start driver onroad test assessment:", result.message)
        }
      })
    }
  }

  const handleNext = () => {
    // Calculate section scores based on the current answer data
    if (currentAnswerData && currentAnswerData.section) {
      const section = currentAnswerData.section
      const currentSectionScore = sectionScores[section] || {
        total: 0,
        correct: 0,
      }

      let totalToAdd = 1 // Default for text-choice
      const correctToAdd = currentAnswerData.score || 0

      // Determine total based on question type
      if (currentAnswerData.questionType === "visual-reaction" && currentAnswerData.totalTargets) {
        totalToAdd = currentAnswerData.totalTargets
      } else if (
        ["parcel-sort-asc", "parcel-sort-desc"].includes(currentAnswerData.questionType || "") &&
        currentAnswerData.displayCount
      ) {
        totalToAdd = currentAnswerData.displayCount
      } else if (
        currentAnswerData.questionType === "color-match" &&
        currentAnswerData.roundsLength
      ) {
        totalToAdd = currentAnswerData.roundsLength
      } else if (
        currentAnswerData.questionType === "route-accuracy" &&
        currentAnswerData.parcelsLength
      ) {
        totalToAdd = currentAnswerData.parcelsLength
      } else if (
        currentAnswerData.questionType === "dynamic-route-planning" &&
        currentAnswerData.endPointsLength
      ) {
        totalToAdd = currentAnswerData.endPointsLength
      }

      // Update section scores
      setSectionScores({
        ...sectionScores,
        [section]: {
          total: currentSectionScore.total + totalToAdd,
          correct: currentSectionScore.correct + correctToAdd,
        },
      })
    }

    // Move to the next question
    if (currentQuestion < quizData.length) {
      const nextQuestionNumber = currentQuestion + 1
      setCurrentQuestion(nextQuestionNumber)

      // Update the driver onroad test assessment with the new question number
      updateDriverOnroadQuestion(nextQuestionNumber).then((result) => {
        if (!result.success) {
          console.error("Failed to update driver onroad test question:", result.message)
        }
      })

      // Reset current answer data for the next question
      setCurrentAnswerData(null)
    }
  }

  console.log("Answers", answers, sectionScores)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Calculate total score from all answers
      let totalScore = 0
      const totalPossibleScore = calculateTotalPossibleScore()

      // Format answers for submission with detailed breakdown
      const formattedAnswers: Record<string, string> = {}
      const detailedScores: Record<
        string,
        {
          score: number
          section: string
          type: string
        }
      > = {}
      const allReactionTimes: number[] = []

      Object.entries(answers).forEach(([questionId, answer]) => {
        const qId = Number(questionId)
        const questionData = quizData.find((q) => q.id === qId)

        if (!questionData) return

        // Convert complex answer types to JSON strings for storage
        formattedAnswers[qId.toString()] =
          typeof answer === "object" ? JSON.stringify(answer) : String(answer)

        // Add to detailed scores
        if (typeof answer === "object") {
          if ("score" in answer) {
            totalScore += answer.score as number
            detailedScores[qId.toString()] = {
              score: answer.score,
              section: questionData.section,
              type: questionData.type,
            }

            // Collect reaction times for average calculation
            if ("reactionTimes" in answer && Array.isArray(answer.reactionTimes)) {
              allReactionTimes.push(...(answer.reactionTimes as number[]))
            }
          } else if (Array.isArray(answer)) {
            let questionScore = 0
            answer.forEach((roundResult) => {
              if (roundResult && typeof roundResult === "object" && "score" in roundResult) {
                questionScore += roundResult.score as number

                // Collect reaction times for average calculation
                if ("reactionTimes" in roundResult && Array.isArray(roundResult.reactionTimes)) {
                  allReactionTimes.push(...(roundResult.reactionTimes as number[]))
                }
              }
            })
            totalScore += questionScore
            detailedScores[qId.toString()] = {
              score: questionScore,
              section: questionData.section,
              type: questionData.type,
            }
          }
        }
      })

      // Calculate the normalized score (0-100)
      const normalizedScore = Math.round((totalScore / totalPossibleScore) * 100)

      // Determine risk level
      const riskLevel = determineRiskLevel(normalizedScore)

      // Calculate average reaction time if available
      const averageReactionTime =
        allReactionTimes.length > 0
          ? Math.round(allReactionTimes.reduce((a, b) => a + b, 0) / allReactionTimes.length)
          : undefined

      // Create test results object
      const testResults: TestResults = {
        totalScore,
        totalPossibleScore,
        scorePercentage: normalizedScore,
        sectionScores,
        riskLevel,
        averageReactionTime,
      }

      // Create a submission object with the correct structure
      const submissionData = {
        answers: formattedAnswers,
        detailedScores,
        sectionScores,
        testResults,
      }

      // Submit assessment results using cloud function
      if (application?.applicationId) {
        const result = await submitDriverOnroadTest(
          application.applicationId,
          normalizedScore,
          submissionData,
        )

        if (!result.success) {
          toast({
            title: "Submission Error",
            description: result.message || "Failed to submit assessment results",
            variant: "destructive",
          })
          return
        }
        const passed = result.data?.result === "pass"
        // Handle success
        if (passed) {
          toast({
            title: "Assessment Passed",
            description: "You have successfully passed the driver onroad assessment!",
          })
        } else {
          toast({
            title: "Assessment Failed",
            description: "Unfortunately, you did not pass the driver onroad assessment.",
            variant: "destructive",
          })
        }
        await refetch()
        // Navigate to next stage if provided
        if (result.data?.nextStage) {
          navigate(`/${result.data.nextStage}`)
        }
      } else {
        toast({
          title: "Submission Error",
          description: "Application ID not found. Please try again later.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Error submitting assessment:", error)
      toast({
        title: "Submission Error",
        description: "There was an error submitting your assessment. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!started) {
    return (
      <Card className="p-6 shadow-sm">
        <h2 className="mb-4 text-xl font-semibold">Driver Onroad Assessment</h2>
        <div className="prose mb-6 max-w-none">
          <p className="mb-4 text-gray-700">
            This test evaluates your knowledge, judgment, and understanding of key principles
            required for safe and professional driving. Please answer all questions to the best of
            your ability.
          </p>
        </div>
        <Button
          onClick={handleStartQuiz}
          className="w-full bg-honeycomb text-white hover:bg-amber-500"
        >
          Start Quiz
        </Button>
      </Card>
    )
  }

  const activeQuestion = quizData.find((q) => q.id === currentQuestion)
  if (!activeQuestion) return null

  const progress = (currentQuestion / quizData.length) * 100

  return (
    <Card className="p-5 shadow-sm">
      <div className="mb-4">
        <div className="h-2 w-full rounded-full bg-gray-200">
          <div
            className="h-2 rounded-full bg-honeycomb transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <p className="mt-1 text-right text-sm text-gray-500">
          Question {currentQuestion} of {quizData.length}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <p className="mb-2 w-full py-2 font-semibold text-gray-900">{activeQuestion.section}</p>
          </div>
          {activeQuestion.type === "text-choice" && (
            <TextChoice
              question={activeQuestion.question}
              options={activeQuestion.options}
              onAnswer={(option) => {
                const isCorrect = option === activeQuestion.answer
                const score = isCorrect ? 1 : 0

                // Store current answer data for section score calculation in handleNext
                setCurrentAnswerData({
                  option,
                  isCorrect,
                  score,
                  section: activeQuestion.section,
                  questionType: activeQuestion.type,
                })

                // Only store the answer without calculating section scores
                setAnswers({
                  ...answers,
                  [currentQuestion]: {
                    score,
                    answer: option,
                    isCorrect,
                  },
                })
              }}
              correctAnswer={activeQuestion.answer}
            />
          )}
          {activeQuestion.type === "visual-reaction" && (
            <VisualReaction
              key={activeQuestion.id}
              question={activeQuestion.question}
              sequence={activeQuestion.sequence}
              targetColor={activeQuestion.targetColor}
              minDelay={activeQuestion.minDelay}
              maxDelay={activeQuestion.maxDelay}
              onComplete={(score, reactionTimes) => {
                const roundResult = { score, reactionTimes }

                // For visual-reaction, total is the number of target colors in the sequence
                const totalTargets = activeQuestion.sequence.filter(
                  (color) => color === activeQuestion.targetColor,
                ).length

                // Store current answer data for section score calculation in handleNext
                setCurrentAnswerData({
                  score,
                  reactionTimes,
                  section: activeQuestion.section,
                  questionType: activeQuestion.type,
                  totalTargets,
                })

                // Only store the answer without calculating section scores
                setAnswers({
                  ...answers,
                  [currentQuestion]: roundResult,
                })
              }}
            />
          )}
          {(activeQuestion.type === "parcel-sort-asc" ||
            activeQuestion.type === "parcel-sort-desc") && (
            <ParcelSort
              sortType={activeQuestion.type === "parcel-sort-asc" ? "asc" : "desc"}
              question={activeQuestion.question}
              rangeStart={activeQuestion.rangeStart}
              rangeEnd={activeQuestion.rangeEnd}
              displayCount={activeQuestion.displayCount}
              onComplete={(score, timeTaken) => {
                // Store current answer data for section score calculation in handleNext
                setCurrentAnswerData({
                  score,
                  timeTaken,
                  section: activeQuestion.section,
                  questionType: activeQuestion.type,
                  displayCount: activeQuestion.displayCount,
                })

                // Only store the answer without calculating section scores
                setAnswers({
                  ...answers,
                  [currentQuestion]: { score, timeTaken },
                })
              }}
            />
          )}
          {activeQuestion.type === "color-match" && (
            <ColorMatch
              question={activeQuestion.question}
              rounds={
                activeQuestion.rounds as {
                  instruction: string
                  targetColor: string
                  targetText: string
                  choices: { color: string; text: string }[]
                }[]
              }
              onComplete={(score) => {
                const roundResult = { score }

                // Store current answer data for section score calculation in handleNext
                setCurrentAnswerData({
                  score,
                  section: activeQuestion.section,
                  questionType: activeQuestion.type,
                  roundsLength: activeQuestion.rounds.length,
                })

                // Only store the answer without calculating section scores
                setAnswers({
                  ...answers,
                  [currentQuestion]: roundResult,
                })
              }}
            />
          )}
        </div>

        {activeQuestion.type === "route-accuracy" && (
          <RoutePlanning
            question={activeQuestion.question}
            parcels={activeQuestion.parcels}
            mapPoints={activeQuestion.mapPoints}
            onComplete={(score, timeTaken) => {
              // Store current answer data for section score calculation in handleNext
              setCurrentAnswerData({
                score,
                timeTaken,
                section: activeQuestion.section,
                questionType: activeQuestion.type,
                parcelsLength: activeQuestion.parcels.length,
              })

              // Only store the answer without calculating section scores
              setAnswers({
                ...answers,
                [currentQuestion]: { score, timeTaken },
              })
            }}
          />
        )}

        {activeQuestion.type === "dynamic-route-planning" && (
          <DynamicRoutePlanning
            question={activeQuestion.question}
            mapGridSize={activeQuestion.mapGridSize}
            rounds={activeQuestion.rounds as Round[]}
            onComplete={(score, timeTaken) => {
              // Store results as an array for multi-round questions
              const roundResult = { score, timeTaken }

              // Use type guard to check if round has endPoints property
              const totalHousesInRounds = activeQuestion.rounds.reduce((total, round) => {
                // Check if this round has endPoints property (dynamic route planning round)
                if ("endPoints" in round) {
                  return total + round.endPoints.length
                }
                return total
              }, 0)

              // Store current answer data for section score calculation in handleNext
              setCurrentAnswerData({
                score,
                timeTaken,
                section: activeQuestion.section,
                questionType: activeQuestion.type,
                endPointsLength: totalHousesInRounds,
              })

              // Only store the answer without calculating section scores
              setAnswers({
                ...answers,
                [currentQuestion]: Array.isArray(answers[currentQuestion])
                  ? [...answers[currentQuestion], roundResult]
                  : [roundResult],
              })
            }}
          />
        )}

        <div className="flex justify-end pt-4">
          {currentQuestion < quizData.length ? (
            <Button
              type="button"
              onClick={handleNext}
              className="bg-honeycomb text-white hover:bg-amber-500"
              disabled={!answers[currentQuestion]}
            >
              Next
            </Button>
          ) : (
            <Button
              type="submit"
              className="bg-honeycomb text-white hover:bg-amber-500"
              disabled={!quizData.every((q) => answers[q.id]) || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                  Submitting...
                </>
              ) : (
                "Submit Quiz"
              )}
            </Button>
          )}
        </div>
      </form>
    </Card>
  )
}

export default DriverOnroadTest
